import * as React from "react";
import * as styles from "./Rotator.css";
import { IRotatorData, IPresentationContext, IARotatorService } from "./Rotator.service";
import { IALikes } from "../../components/likes/Likes";
import { IAComments } from "../../components/comments/Comments";
import { ILike } from "../../interfaces/ILike";
import { ConditionalOutput } from "../../services/ConditionalOutput";
import { IAIcon } from "../icon/Icon";
import DateTimeService from "../../services/DateTimeService";
import { Helper } from "../../Helper";
import { Swipeable } from "react-swipeable";
import { LocalizationService } from "../../services/LocalizationService";
import { IAUniqueViews } from "../uniqueViews/UniqueViews";
import { Environment } from "../../services/Environment";
import { IAMessageVideo } from "../messages/video/MessageVideo";
import { IEnvironment } from "../../interfaces/IEnvironment";
import { IAMessageCategories } from "../viewer/categories/MessageCategories";
import { IFontSlots } from "../../interfaces/IFontSlots";
import Moment from "react-moment";
import { ENTER_KEY, SPACEBAR_KEY } from "../../global/Constants";

export interface IARotatorProps {
  defaultImage?: string;
  data?: IRotatorData[];
  showSkeleton?: boolean;
  width?: number;
  showTitle?: boolean;
  showCategories?: boolean;
  showTeaser?: boolean;
  showAuthor?: boolean;
  showStartDate?: boolean;
  showLikes?: boolean;
  showComments?: boolean;
  showTimespand?: boolean;
  showDropShadow?: boolean;
  borderRadius?: number,
  borderColor?: string;
  isMobile?: boolean;
  component?: string;
  showUniqueViews?: boolean;
  showLocation?: boolean;
  highlightColor?: string;
  hasDarkBackground?: boolean;
  showPaging?: boolean;
  highlightUnread?: string;
  environment: IEnvironment;
  tenant: string;
  token: string;
  autoRotate?: boolean;
  autoRotateInterval?: number;
  displayDateOnCover?: boolean;
  showLikesDimmed?: boolean;
  showCommentsDimmed?: boolean;
  showUniqueViewsDimmed?: boolean;
  fontSlots?: IFontSlots;
  userLocation?: string;
  reactionOptions?: string[];
  showEmojiPickerExpandButton?: boolean;
}
export interface IARotatorState {
  isHovering: boolean;
  currentCardNumber: number;
  defaultImage: string;
  imageFormat: number;
}

export class IARotator extends React.Component<IARotatorProps, IARotatorState> {
  private rotatorService: IARotatorService = new IARotatorService();
  private dateTimeService: DateTimeService = new DateTimeService();
  private localizationService: LocalizationService = new LocalizationService();
  private rotateTimer: number;
  private rotatorRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(public props: IARotatorProps, public state: IARotatorState) {
    super(props);
    this.state = {
      isHovering: false,
      currentCardNumber: 0,
      defaultImage: !this.props.defaultImage ? "https://spoprod-a.akamaihd.net/files/sp-client-prod_2019-09-27.007/spdefaultbanner_light_a0bc58c25323ac7ccd8b6d2e15b0ee85.jpg" : this.props.defaultImage,
      imageFormat: 16 / 9,
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidUpdate(prevProps: Readonly<IARotatorProps>): void {
    if (prevProps.autoRotate !== this.props.autoRotate || prevProps.data !== this.props.data || prevProps.autoRotateInterval !== this.props.autoRotateInterval) {
      if (this.props.autoRotate) {
        this.stopTimer();
        this.startTimer();
      } else {
        this.stopTimer();
      }
    }
  }

  private startTimer(): void {
    if (this.props.data.length > 0 && this.props.autoRotate && !this.rotateTimer) {
      this.rotateTimer = window.setInterval(() => {
        if (this.state.currentCardNumber < this.props.data.length - 1) {
          this.setState({ currentCardNumber: this.state.currentCardNumber + 1 });
        } else {
          this.setState({ currentCardNumber: 0 });
        }
      }, this.props.autoRotateInterval || 5000);
    }
  }

  private stopTimer(): void {
    if (this.rotateTimer) {
      clearInterval(this.rotateTimer);
      this.rotateTimer = undefined;
    }
  }

  public render() {
    return (
      <>
        <div
          style={{
            borderRadius: this.props.borderRadius,
            boxShadow: this.props.showDropShadow ? "rgba(0, 0, 0, 0.133) 0px 1.6px 3.6px 0px, rgba(0, 0, 0, 0.11) 0px 0.3px 0.9px 0px" : "none",
            width: this.props.width
          }}
        >
          <Swipeable
            onSwipedRight={() => {
              const i: number = this.state.currentCardNumber - 1;
              this.setState({
                currentCardNumber:
                  this.state.currentCardNumber === 1
                    ? 0
                    : new ConditionalOutput(this.state.currentCardNumber)
                      .OnCondition(i < 0, this.state.currentCardNumber)
                      .OnCondition(i > 0, i)
                      .Last(),
              });
            }}
            onSwipedLeft={() => {
              const i: number = this.state.currentCardNumber + 1;
              this.setState({
                currentCardNumber: new ConditionalOutput(this.state.currentCardNumber)
                  .OnCondition(i >= this.props.data.length, this.state.currentCardNumber)
                  .OnCondition(i < this.props.data.length, i)
                  .Last(),
              });
            }}
          >
            <div
              ref={this.rotatorRef}
              onMouseEnter={() => {
                this.setState({ isHovering: true });
                this.stopTimer();
              }}
              onMouseLeave={() => {
                this.setState({ isHovering: false });
                this.startTimer();
              }}
              onFocus={() => {
                this.setState({ isHovering: true });
                this.stopTimer();
              }}
              onBlur={(event) => {
                if (!this.rotatorRef.current?.contains(event.relatedTarget as Node)) {
                  this.setState({ isHovering: false });
                  this.startTimer();
                }
              }}
            >
              {!this.props.showSkeleton && (
                <div>
                  {this.props.data && (
                    <div className={[styles.wrapper, styles.pointer].join(" ")} style={{ width: "inherit" }}>
                      <div className={styles.inheritDiv}>
                        <div id={"styleComponent"} className={styles.slider}>
                          {
                            <div className={styles.inheritDiv}>
                              <div
                                id={styles.arrowContainer}
                                style={{
                                  opacity: this.state.isHovering || this.props.isMobile ? 1 : 0,
                                  width: "100%",
                                }}
                              >
                                <div
                                  className={[styles.leftArrow, styles.pointer, "IA_OnFocus"].join(" ")}
                                  tabIndex={this.state.currentCardNumber != 0 ? 0 : -1}
                                  aria-label="Previous"
                                  style={{
                                    opacity: this.state.currentCardNumber != 0 ? 1 : 0,
                                    marginTop: new ConditionalOutput(this.props.width)
                                      .OnCondition(this.props.width >= 800, 280)
                                      .OnCondition(!(this.props.width >= 800), (this.props.width / this.state.imageFormat + 50) / 2)
                                      .Last(),
                                  }}
                                  onClick={(v) => {
                                    v.stopPropagation();
                                    const i: number = this.state.currentCardNumber - 1;
                                    this.setState({
                                      currentCardNumber:
                                        this.state.currentCardNumber === 1
                                          ? 0
                                          : new ConditionalOutput(this.state.currentCardNumber)
                                            .OnCondition(i < 0, this.state.currentCardNumber)
                                            .OnCondition(i > 0, i)
                                            .Last(),
                                    });
                                  }}
                                  onKeyDown={(event) => {
                                    if ((event.key === ENTER_KEY || event.key === SPACEBAR_KEY) && this.state.currentCardNumber != 0) {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      const i: number = this.state.currentCardNumber - 1;
                                      this.setState({
                                        currentCardNumber:
                                          this.state.currentCardNumber === 1
                                            ? 0
                                            : new ConditionalOutput(this.state.currentCardNumber)
                                              .OnCondition(i < 0, this.state.currentCardNumber)
                                              .OnCondition(i > 0, i)
                                              .Last(),
                                      });
                                    }
                                  }}
                                >
                                  <div className={styles.arrowIcon}>
                                    <IAIcon url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Left/SVG/ic_fluent_chevron_left_48_regular.svg" size={25} color={"white"} />
                                  </div>
                                </div>
                                <div
                                  className={[styles.rightArrow, styles.pointer, "IA_OnFocus"].join(" ")}
                                  tabIndex={this.state.currentCardNumber < this.props.data.length - 1 ? 0 : -1}
                                  aria-label="Next"
                                  style={{
                                    opacity: this.state.currentCardNumber < this.props.data.length - 1 ? 1 : 0,
                                    marginTop: new ConditionalOutput(this.props.width)
                                      .OnCondition(this.props.width >= 800, 280)
                                      .OnCondition(!(this.props.width >= 800), (this.props.width / this.state.imageFormat + 50) / 2)
                                      .Last(),
                                    marginLeft: this.props.width - 30,
                                  }}
                                  onClick={(v) => {
                                    v.stopPropagation();
                                    const i: number = this.state.currentCardNumber + 1;
                                    this.setState({
                                      currentCardNumber: new ConditionalOutput(this.state.currentCardNumber)
                                        .OnCondition(i >= this.props.data.length, this.state.currentCardNumber)
                                        .OnCondition(i < this.props.data.length, i)
                                        .Last(),
                                    });
                                  }}
                                  onKeyDown={(event) => {
                                    if ((event.key === ENTER_KEY || event.key === SPACEBAR_KEY) && this.state.currentCardNumber < this.props.data.length - 1) {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      const i: number = this.state.currentCardNumber + 1;
                                      this.setState({
                                        currentCardNumber: new ConditionalOutput(this.state.currentCardNumber)
                                          .OnCondition(i >= this.props.data.length, this.state.currentCardNumber)
                                          .OnCondition(i < this.props.data.length, i)
                                          .Last(),
                                      });
                                    }
                                  }}
                                >
                                  <div
                                    className={styles.arrowIcon}
                                    style={{
                                      marginLeft: 3
                                    }}
                                  >
                                    <IAIcon url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Right/SVG/ic_fluent_chevron_right_48_regular.svg" size={25} color={"white"} />
                                  </div>{" "}
                                </div>
                              </div>
                            </div>
                          }
                          <div>
                            <div
                              id={styles.rotatorContentWrapper}
                              style={{
                                width: `${this.props.data.length * 100}%`,
                                marginLeft: `-${this.state.currentCardNumber * 100}%`,
                              }}
                            >
                              {this.props.data && this.props.data.map((rotatorData, i) => this.itemRender(rotatorData, i))}
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              )}
              {this.props.showSkeleton && (
                <div
                  className={styles.slider}
                  style={{
                    width: "100%",
                    overflow: "overlay",
                    height: this.props.width != undefined ? ((this.props.width / 16) * 9) + this.getComponentHeight() + 34 : 0,
                    opacity: this.props.hasDarkBackground ? 0.1 : 1
                  }}
                >
                  <div
                    className={styles.coverImageLoader}
                    style={{
                      height: this.props.width != undefined ? this.props.width / this.state.imageFormat : 0,
                      maxHeight: 500,
                    }}
                  />
                  <div className={styles.titleLoader} />
                  <div className={styles.teaserLoader} />
                  <div className={styles.teaserLoader} />
                  <div className={styles.teaserLoader} />
                </div>
              )}
            </div>
          </Swipeable>
        </div>
        {
          this.props.showPaging &&
          <div className={styles.dots}>
            {this.props.data.map((_v, i) => {
              return (
                <div
                  className={[styles.dot, styles.pointer, "IA_OnFocus"].join(" ")}
                  tabIndex={this.state.currentCardNumber === i ? -1 : 0}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => {
                    this.setState({ currentCardNumber: i });
                  }}
                  onKeyDown={(event) => {
                    if ((event.key === ENTER_KEY || event.key === SPACEBAR_KEY) && this.state.currentCardNumber !== i) {
                      event.preventDefault();
                      event.stopPropagation();
                      this.setState({ currentCardNumber: i });
                    }
                  }}
                  key={`dot${i}`}
                  style={{
                    background:
                      this.state.currentCardNumber === i
                        ? this.props.highlightColor || "#333333"
                        : "rgba(230, 230, 230, 1)",
                  }}
                />
              );
            })}
          </div>
        }
      </>
    );
  }

  private getComponentHeight(): number {
    let minHeight = 0;
    let iconsAmount = 0;
    if (this.props.showLikes) {
      iconsAmount++;
    }
    if (this.props.showComments) {
      iconsAmount++;
    }
    if (this.props.showUniqueViews) {
      iconsAmount++;
    }
    if (iconsAmount === 3) {
      minHeight = minHeight < 122 ? 122 : minHeight;
    }
    if (iconsAmount === 2) {
      minHeight = minHeight < 90 ? 90 : minHeight;
    }
    if (iconsAmount === 1) {
      minHeight = minHeight < 50 ? 50 : minHeight;
    }
    let rotatorElementHeight = 10;
    if (this.props.component === "Event") {
      if (this.props.showTitle) {
        rotatorElementHeight += 33;
      }
      if (this.props.showTeaser) {
        rotatorElementHeight += 44;
      }
      if (this.props.showCategories) {
        rotatorElementHeight += 30;
      }
      if (this.props.showLocation || this.props.showTimespand) {
        rotatorElementHeight += 26;
      }
    } else {
      if (this.props.showTitle) {
        rotatorElementHeight += 33;
      }
      if (this.props.showTeaser) {
        rotatorElementHeight += 44;
      }
      if (this.props.showCategories) {
        rotatorElementHeight += 30;
      }
      if (this.props.showAuthor || this.props.showStartDate) {
        rotatorElementHeight += 26;
      }
    }
    if (minHeight > rotatorElementHeight) {
      rotatorElementHeight = minHeight;
    }
    return rotatorElementHeight;
  }

  private itemRender = (d: IRotatorData, i: number): JSX.Element => {
    const presentationContext: IPresentationContext = this.rotatorService.getPresentationContext(
      d,
      { selectedCardIndex: this.state.currentCardNumber },
      { width: 0 },
      i
    );
    const item = d.item
    const liStyles = { ...(d.styles || {}) };
    if (d.videoUrl && (d.videoUrl.indexOf("stream") != -1 || (d.videoUrl.indexOf("sharepoint") != -1 && location.href.indexOf("sharepoint.com") == -1)) && d.image == undefined && this.props.isMobile) {
      d.image = "https://amsglob0cdnstream13.azureedge.net/streamlogo/Light_640x360.jpg";

    }
    let textColor: string = d.backgroundColor != undefined && d.backgroundColor != null ? Helper.getTextColorBasedOnBackgroundColorDarkness(d.backgroundColor, "#ffffff", "#333333") : "#333333";
    textColor = d.itemTextColor != undefined ? d.itemTextColor : textColor;
    if (d.itemTextColor && Helper.getTextColorBasedOnBackgroundColorDarkness(d.itemTextColor, false, true) && d.backgroundColor == undefined && !this.props.hasDarkBackground) {
      textColor = "#333333";
    }
    if (d.backgroundColor && d.backgroundColor !== "transparent" && Helper.getTextColorBasedOnBackgroundColorDarkness(d.backgroundColor, "#ffffff", "#333333") === Helper.getTextColorBasedOnBackgroundColorDarkness(textColor, "#ffffff", "#333333")) {
      textColor = Helper.getTextColorBasedOnBackgroundColorDarkness(d.backgroundColor, "#ffffff", "#333333");
    }
    if (!d.backgroundColor && !d.itemTextColor) {
      // if both background color and text color is undefined, use the color from sharepoint
      textColor = undefined;
    }
    liStyles.backgroundColor = new ConditionalOutput(d.backgroundColor).OnCondition(!d.backgroundColor, "transparent").Last();
    liStyles.color = new ConditionalOutput("").OnCondition(!!d.itemTextColor && !presentationContext.isSelected, textColor).Last();
    let rotatorElementHeight = this.getComponentHeight();
    const isEventToday =
      item && this.props.component === "Event"
        ? this.dateTimeService.ConvertToDDMMYYYY(item.startDate) === this.dateTimeService.ConvertToDDMMYYYY(new Date())
        : undefined;
    const isOneDayEvent = item && this.props.component === "Event" && this.dateTimeService.isStartAndEndDateTheSameDay(item.startDate, item.endDate, item.allDayEvent);
    const hasCategories = this.props.showCategories && presentationContext.categories?.length > 0;
    const showDateOnCover = item && this.props.component === "Event" && this.props.displayDateOnCover;
    const isCurrentSlide = i === this.state.currentCardNumber;
    const rotatorItem = (
      <div
        className={[styles.slidesWrapper, "IA_OnFocus"].join(" ")}
        key={i}
        tabIndex={isCurrentSlide ? 0 : -1}
        aria-hidden={!isCurrentSlide}
        {...(!isCurrentSlide ? ({ inert: "" } as any) : {})}
        onClick={d.image || (!d.image && !d.videoUrl) ? () => d.onClickOpen(d.item) : undefined}
        onKeyDown={(event) => {
          if ((event.key === ENTER_KEY || event.key === SPACEBAR_KEY) && d.onClickOpen) {
            event.preventDefault();
            d.onClickOpen(d.item);
            event.currentTarget.blur();
          }
        }}
        style={{
          width: this.props.width,
          borderColor: this.props.borderColor ?? "transparent",
          borderRadius: this.props.borderRadius,
          ...liStyles
        }}
      >
        {(this.props.highlightUnread && d.isUnread) && <div style={{ position: 'absolute', borderLeft: `6px solid ${this.props.highlightUnread ? this.props.highlightUnread : "#ffb900"}`, height: "100%", zIndex: 1 }}></div>}
        <div className={styles.wrapper}>
          {d.showPinIcon &&
            <div
              className={styles.IA_pinCoverOverlay}
              style={{
                cursor: d.onClickOpen ? "pointer" : undefined
              }}
              onClick={d.onClickOpen ? () => d.onClickOpen(d) : undefined}
            >
              <img
                src="https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/pinned.png"
                alt=""
                style={{
                  width: 22,
                  height: 22
                }}
              />
            </div>
          }
          <div
            className={styles.rotatorBackgroundImageWrapper}
            style={{
              height: this.props.width != undefined ? `calc( ${this.props.width}px / 16 * 9)` : 0,
            }}
          >
            {this.props.component !== "Event" && d.videoUrl != undefined && d.image == undefined && ((d.videoUrl.indexOf("microsoftstream") != -1 || d.videoUrl.indexOf("sharepoint.com") != -1) && location.href.indexOf("sharepoint.com") != -1) && (
              <div className={[styles.rotatorBackgroundVideo, styles.pointer].join(" ")}>
                {d.videoUrl.indexOf("youtube") !== -1 ?
                  <iframe width="100%" height="100%" allowFullScreen src={d.videoUrl} />
                  :
                  <video
                    height="100%"
                    width="100%"
                    poster={d.image}
                    disablePictureInPicture
                    controlsList="nodownload noplaybackrate"
                    controls
                  >
                    <source src={d.videoUrl}></source>
                  </video>
                }
              </div>
            )}
            {((d.videoUrl && d.image) || d.image) ?
              <div
                className={[styles.rotatorBackgroundImage, styles.pointer].join(" ")}
                style={{
                  backgroundImage:
                    this.props.component === "Event" && !d.image
                      ? undefined
                      : `url('${d.iaMessageBgImagesFromBlob?.StandardLarge ? d.iaMessageBgImagesFromBlob?.StandardLarge : new ConditionalOutput(Helper.getImageUrl(Environment.getEnvironmentForImageAPI(d.environment), d.tenant, d.token, d.image, undefined, "StandardLarge", d.iaConfiguration?.cropSettings)).OnCondition(!d.image, this.state.defaultImage).Last()}')`,
                  backgroundColor: this.props.component === "Event" ? "rgb(3, 120, 124)" : undefined,
                  backgroundPosition: "50% 50%",
                  backgroundSize: "cover",
                }}
              />
              :
              <IAMessageVideo
                height={"100%"}
                width={"100%"}
                videoUrl={d.videoUrl}
                environment={this.props.environment}
                tenant={this.props.tenant}
                token={this.props.token}
              />
            }
            {showDateOnCover && !isEventToday && !isOneDayEvent && (
              <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.borderRadius }}>
                <div className={styles.IA_startDate}>
                  {!item.allDayEvent
                    ? `${this.dateTimeService.getDayInMonth(item.startDate)}. ${this.dateTimeService.getNameOfMonth(item.startDate, item.allDayEvent)?.substring(0, 3)?.toUpperCase()}`
                    : `${this.dateTimeService.getEventString(item.startDate, item.endDate, item.allDayEvent).substring(0, 2)}. ${this.dateTimeService.getNameOfMonth(item.startDate, item.allDayEvent).substring(0, 3)?.toUpperCase()}`}
                </div>
                <div className={styles.IA_startDateendDateDivider} />
                <div className={styles.IA_endDate}>
                  {!item.allDayEvent
                    ? `${this.dateTimeService.getDayInMonth(item.endDate)}. ${this.dateTimeService
                      .getNameOfMonth(item.endDate, item.allDayEvent)?.substring(0, 3)?.toUpperCase()}`
                    : `${this.dateTimeService.getEventString(item.startDate, item.endDate, item.allDayEvent).substring(8, 10)}. ${this.dateTimeService.getNameOfMonth(item.endDate, item.allDayEvent).substring(0, 3)?.toUpperCase()}`}
                </div>
              </div>
            )}
            {showDateOnCover && !isEventToday && isOneDayEvent && (
              <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.borderRadius }}>
                <div className={styles.IA_coverOverlayMonth}>{this.dateTimeService.getNameOfMonth(item.startDate)?.substring(0, 3)?.toUpperCase()}</div>
                <div className={styles.IA_coverOverlayDay}>
                  {!item.allDayEvent ? this.dateTimeService.getDayInMonth(item.startDate, item.allDayEvent) : this.dateTimeService.getEventString(item.startDate, item.endDate, item.allDayEvent).substring(0, 2)}
                </div>
              </div>
            )}
            {showDateOnCover && isEventToday && isOneDayEvent && (
              <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.borderRadius }}>
                <div className={styles.IA_coverOverlayToday}>{this.localizationService.strings.DateTimeService_Today?.toUpperCase()}</div>
              </div>
            )}
            {showDateOnCover && isEventToday && !isOneDayEvent && (
              <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.borderRadius }}>
                <div className={styles.IA_coverOverlayDay} style={{ marginTop: "11px" }}>
                  {this.localizationService.strings.DateTimeService_Today?.toUpperCase()}
                </div>
                <div className={styles.IA_startDateendDateDivider} />
                <div className={styles.IA_endDate}>
                  {!item.allDayEvent
                    ? `${this.dateTimeService.getDayInMonth(item.endDate)}. ${this.dateTimeService
                      .getNameOfMonth(item.endDate, item.allDayEvent)?.substring(0, 3)?.toUpperCase()}`
                    : `${this.dateTimeService.getEventString(item.startDate, item.endDate, item.allDayEvent).substring(8, 10)}. ${this.dateTimeService.getNameOfMonth(item.endDate, item.allDayEvent)?.substring(0, 3)?.toUpperCase()}`}
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              paddingLeft: 10,
              boxSizing: "border-box",
              width: this.props.showLikes || this.props.showComments || this.props.showUniqueViews ? "calc(100% - 50px)" : "calc(100% - 10px)",
              float: "left",
              height: rotatorElementHeight != undefined ? rotatorElementHeight : 0,
            }}
            onClick={() => d.onClickOpen(d.item)}
          >
            {this.props.component === "Event" && (
              <div>
                {hasCategories &&
                  <IAMessageCategories
                    categories={d.categories}
                    paddingTop={5}
                    showOverflowCounter
                    fontSlots={this.props.fontSlots}
                    disablePopup={this.props.isMobile}
                    textColorWhenTransparentBackground={textColor}
                  />
                }
                {(this.props.showAuthor || this.props.showStartDate) && (
                  <div
                    className={[styles.metaData, styles.pointer].join(" ")}
                    style={{
                      color: textColor,
                      fontFamily: this.props.fontSlots?.body?.fontFamily
                    }}
                  >
                    {this.props.showAuthor && <span style={{ fontWeight: "bold" }}>{`${d.author ? d.author?.displayName + " " : ""}`}</span>}
                    <Moment
                      format="L"
                      locale={this.props.userLocation ?? navigator.language}
                    >
                      {d.modifiedDate}
                    </Moment>
                  </div>
                )}
                {this.props.showTitle && (
                  <div
                    className={[styles.title, styles.pointer].join(" ")}
                    style={{
                      marginTop: hasCategories ? 0 : 5,
                      color: textColor,
                      marginBottom: 5,
                      fontFamily: this.props.fontSlots?.heading?.fontFamily
                    }}
                  >
                    {d.title}
                  </div>
                )}
                <div>
                  {this.props.showTimespand && this.props.component === "Event" && (
                    <div
                      className={[styles.IA_timespand, styles.pointer].join(" ")}
                      style={{
                        color: textColor,
                        marginTop: 0,
                        fontFamily: this.props.fontSlots?.body?.fontFamily
                      }}
                    >
                      {d.timespand}
                    </div>
                  )}
                  {this.props.showLocation && (
                    <div
                      className={[styles.IA_locationText, styles.pointer].join(" ")}
                      style={{
                        color: textColor,
                        fontFamily: this.props.fontSlots?.body?.fontFamily
                      }}
                    >
                      {d.item.location}
                    </div>
                  )}
                </div>
              </div>
            )}
            {this.props.component !== "Event" && (this.props.showTitle || this.props.showTeaser || this.props.showAuthor || this.props.showStartDate || this.props.showCategories) && (
              <div
                style={{
                  paddingLeft: 10,
                  boxSizing: "border-box",
                  width: this.props.showLikes || this.props.showComments || this.props.showUniqueViews ? "calc(100% - 50px)" : "calc(100% - 10px)",
                  float: "left",
                  height: rotatorElementHeight != undefined ? rotatorElementHeight : 0,
                }}
              >
                {hasCategories &&
                  <IAMessageCategories
                    categories={d.categories}
                    paddingTop={5}
                    showOverflowCounter
                    fontSlots={this.props.fontSlots}
                    disablePopup={this.props.isMobile}
                    textColorWhenTransparentBackground={textColor}
                  />
                }
                {this.props.showTitle && (
                  <div
                    className={[styles.title, styles.pointer].join(" ")}
                    style={{
                      marginTop: hasCategories ? 5 : 10,
                      color: textColor,
                      fontFamily: this.props.fontSlots?.heading?.fontFamily
                    }}
                  >
                    {d.title}
                  </div>
                )}
                {this.props.showTeaser && (
                  <div
                    className={[styles.teaser, styles.pointer].join(" ")}
                    style={{
                      color: textColor,
                      fontFamily: this.props.fontSlots?.body?.fontFamily
                    }}
                  >
                    {d.teaser}
                  </div>
                )}
                {(this.props.showAuthor || this.props.showStartDate) && (
                  <div
                    className={[styles.metaData, styles.pointer].join(" ")}
                    style={{
                      color: textColor,
                      fontFamily: this.props.fontSlots?.body?.fontFamily
                    }}
                  >
                    {!d.iaConfiguration?.fieldsToHide?.includes("author") && this.props.showAuthor && <span style={{ fontWeight: "bold" }}>{`${d.author ? d.author?.displayName + " " : ""}`}</span>}
                    {this.props.showStartDate ?
                      <Moment
                        format="L"
                        locale={this.props.userLocation ?? navigator.language}
                      >
                        {d.modifiedDate}
                      </Moment>
                      : undefined
                    }
                  </div>
                )}
              </div>
            )}
          </div>

          {(this.props.showLikes || this.props.showComments || this.props.showUniqueViews) && (
            <div
              className={styles.IA_commandBar}
              style={{
                marginTop: 10,
                height: rotatorElementHeight != undefined ? rotatorElementHeight - 20 : 0,
                borderColor: `${d.itemTextColor}20`
              }}
            >
              {this.props.showLikes && !d.iaConfiguration?.disableLikes && d.likesEnabled && (
                <div
                  key={"likes-" + 1}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IALikes
                    highlightColor={d.highlightColor}
                    color={d.itemTextColor}
                    itemId={d.id}
                    user={d.user}
                    tenant={d.tenant}
                    component={this.props.component === "Messages" ? "Message" : "Event"}
                    instance={d.instance}
                    token={d.token}
                    environment={d.environment}
                    source={d.source}
                    likes={d.likes}
                    likesUpdated={(likes: ILike[]) => d.likesUpdated(likes)}
                    dataAutomationId={`messages-rotator-view-like-${1}`}
                    displayVertical={true}
                    showEmojiPickerExpandButton={this.props.showEmojiPickerExpandButton}
                    reactionOptions={this.props.reactionOptions}
                    showDimmed={this.props.showLikesDimmed && !d.iaConfiguration?.disableLikes}
                    isMobile={this.props.isMobile}
                  />
                </div>
              )}
              {this.props.showComments && !d.iaConfiguration?.disableComments && (
                <IAComments
                  key={"comments-" + 1}
                  color={d.highlightColor}
                  itemId={d.id}
                  user={d.user}
                  tenant={d.tenant}
                  component={this.props.component === "Messages" ? "Message" : "Event"}
                  instance={d.instance}
                  token={d.token}
                  environment={d.environment}
                  commentCount={d.commentCount}
                  commentsUpdated={(comments: number) => d.commentsUpdated(comments)}
                  commentsClick={() => d.commentsClick(d.item)}
                  displayVertical={true}
                  showDimmed={this.props.showCommentsDimmed && !d.iaConfiguration.isNewsLink && !d.iaConfiguration?.disableComments}
                />
              )}
              {this.props.showUniqueViews && (
                <IAUniqueViews
                  color={d.highlightColor}
                  itemId={d.id}
                  user={d.user}
                  tenant={d.tenant}
                  component={d.component === "Messages" ? "Message" : "Event"}
                  instance={d.instance}
                  token={d.token}
                  environment={d.environment}
                  source={d.source}
                  uniqueViews={d.uniqueViewsCount}
                  displayVertical={true}
                  showDimmed={this.props.showUniqueViewsDimmed && this.dateTimeService.isOlderThanOneHour(d.modifiedDate)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
    return d.link ?
      <a href={d.link} target="_blank">{rotatorItem}</a>
      :
      rotatorItem;
  };
}
